import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Logo } from "components/logo";
import { motion } from "framer-motion";
import { openErrorNotification } from "helpers/methods";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  boldFont,
  getRelativeFontSize,
  mediumFont,
  regularFont,
} from "utils/styles";
import { setAuthUserIdInStorage } from "screens/Blogs/blogsUserPreferences";
import { loginAction } from "../../redux/authSlice";
import { loginUser } from "./AuthServices";
import ForgotPassword from "./ForgotPassword";
import { getLoginStyles } from "./Login.styles";
import SignUp from "./SignUp";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const Login: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation<{ redirectTo?: string }>();
  const classes = getLoginStyles(theme);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const dispatch = useDispatch();

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(email)) newErrors.email = "Invalid email";

    if (!password) newErrors.password = "Password is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) return;

    setServerError("");
    setLoading(true);

    try {
      const resp: any = await loginUser(email, password);
      const userId = resp?.user?.id || "";
      dispatch(
        loginAction({
          authenticated: true,
          accessToken: resp.access_token,
          refreshToken: resp.refresh_token || "",
          userName: resp?.user?.name,
          email: resp?.user?.email || email,
          isAdmin: resp?.user?.isAdmin || false,
          id: userId,
          firstName: resp?.user?.firstName || "",
          lastName: resp?.user?.lastName || "",
        })
      );
      setAuthUserIdInStorage(userId);

      const redirectTo = location.state?.redirectTo;
      history.push(
        typeof redirectTo === "string" && redirectTo.startsWith("/")
          ? redirectTo
          : "/"
      );
      return;
    } catch (error: any) {
      const msg = error?.message || error?.errorMessage || "Operation failed";
      setServerError(String(msg));
      openErrorNotification(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes authPulse {
          0%, 100% { opacity: 0.9; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.04); }
        }
        @keyframes authFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-12px) translateX(8px) rotate(5deg); }
          66% { transform: translateY(8px) translateX(-6px) rotate(-3deg); }
        }
      `}</style>
      <Box sx={classes.root}>
        {/* Background: gradient overlay + orbs + grid + floating shapes */}
        <Box sx={classes.backgroundLayer}>
          <Box sx={classes.gradientOverlay} />
          <Box sx={classes.orb1} />
          <Box sx={classes.orb2} />
          <Box sx={classes.orb3} />
          <Box sx={classes.gridOverlay} />
          <Box sx={classes.floatShape1} aria-hidden />
          <Box sx={classes.floatShape2} aria-hidden />
        </Box>

        {/* Single centered card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: "100%", maxWidth: 440 }}
        >
          <Box sx={classes.cardWrapper}>
            <Paper elevation={0} component={Box} sx={classes.card}>
              <Box sx={classes.cardInner}>
                <Box sx={classes.logoRow}>
                  <Box sx={{ width: 42, height: 42 }}>
                    <Logo />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 1.5,
                      ...boldFont,
                      fontSize: getRelativeFontSize(2),
                      background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Panacea Insights
                  </Typography>
                </Box>

                <Box sx={classes.welcomeBlock}>
                  <Typography
                    variant="h5"
                    sx={{
                      ...boldFont,
                      fontSize: getRelativeFontSize(4),
                      color: theme.palette.text.primary,
                      mb: 0.5,
                    }}
                  >
                    {isForgotPassword
                      ? "Reset your password"
                      : isSignUp
                      ? "Create your account"
                      : "Welcome back 👋"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      ...regularFont,
                      fontSize: getRelativeFontSize(0),
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {isForgotPassword
                      ? "Enter your email and we'll send you a reset link"
                      : isSignUp
                      ? "Fill in your details to get started"
                      : "Sign in to access your dashboard and insights"}
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={classes.form}>
                  {isForgotPassword ? (
                    <ForgotPassword onBack={() => setIsForgotPassword(false)} />
                  ) : isSignUp ? (
                    <SignUp onBack={() => setIsSignUp(false)} />
                  ) : (
                    <>
                      <TextField
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        fullWidth
                        sx={{
                          mb: 2,
                          ...classes.textField,
                          "& .MuiOutlinedInput-root": {
                            ...regularFont,
                            fontSize: getRelativeFontSize(0),
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.background.default, 0.5)
                                : alpha(theme.palette.background.paper, 0.9),
                            borderRadius: "12px",
                          },
                          "& .MuiInputBase-input": {
                            color: theme.palette.text.primary,
                          },
                        }}
                        InputLabelProps={{
                          shrink: !!email,
                          sx: {
                            ...regularFont,
                            fontSize: getRelativeFontSize(0),
                            color: theme.palette.text.secondary,
                          },
                        }}
                      />

                      <TextField
                        label="Password"
                        variant="outlined"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!errors.password}
                        helperText={errors.password}
                        fullWidth
                        sx={{
                          mb: 1,
                          ...classes.textField,
                          "& .MuiOutlinedInput-root": {
                            ...regularFont,
                            fontSize: getRelativeFontSize(0),
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.background.default, 0.5)
                                : alpha(theme.palette.background.paper, 0.9),
                            borderRadius: "12px",
                          },
                          "& .MuiInputBase-input": {
                            color: theme.palette.text.primary,
                          },
                        }}
                        InputLabelProps={{
                          shrink: !!password,
                          sx: {
                            ...regularFont,
                            fontSize: getRelativeFontSize(0),
                            color: theme.palette.text.secondary,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword((s) => !s)}
                                edge="end"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  "&:hover": { color: BRAND },
                                }}
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Box sx={classes.actionsRow}>
                        {!isSignUp && !isForgotPassword && (
                          <Button
                            variant="text"
                            onClick={() => setIsForgotPassword(true)}
                            sx={{
                              ...regularFont,
                              fontSize: getRelativeFontSize(0),
                              textTransform: "none",
                              color: theme.palette.text.secondary,
                              "&:hover": {
                                color: BRAND,
                                backgroundColor: alpha(BRAND, 0.06),
                              },
                            }}
                          >
                            Forgot password?
                          </Button>
                        )}
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                          ...mediumFont,
                          ...classes.primaryButton,
                          textTransform: "none",
                          borderRadius: "12px",
                          background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                          boxShadow: `0 4px 20px ${alpha(BRAND, 0.35)}`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`,
                            boxShadow: `0 6px 24px ${alpha(BRAND, 0.45)}`,
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          "Sign in"
                        )}
                      </Button>

                      {serverError && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography
                            color="error"
                            variant="body2"
                            sx={{
                              ...regularFont,
                              fontSize: getRelativeFontSize(0),
                            }}
                          >
                            {serverError}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  <Divider
                    sx={{
                      my: 2.5,
                      borderColor: alpha(theme.palette.divider, 0.15),
                    }}
                  />

                  <Box sx={classes.signUpRow}>
                    <Typography
                      variant="body2"
                      sx={{
                        ...regularFont,
                        fontSize: getRelativeFontSize(0),
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {isForgotPassword ? (
                        <>
                          Remember your password?{" "}
                          <Button
                            variant="text"
                            sx={{
                              ...boldFont,
                              fontSize: getRelativeFontSize(0),
                              textTransform: "none",
                              color: BRAND,
                              "&:hover": {
                                backgroundColor: alpha(BRAND, 0.08),
                              },
                            }}
                            onClick={() => setIsForgotPassword(false)}
                          >
                            Sign in
                          </Button>
                        </>
                      ) : !isSignUp ? (
                        <>
                          New to Panacea Insights?{" "}
                          <Button
                            variant="text"
                            sx={{
                              ...boldFont,
                              fontSize: getRelativeFontSize(0),
                              textTransform: "none",
                              color: BRAND,
                              "&:hover": {
                                backgroundColor: alpha(BRAND, 0.08),
                              },
                            }}
                            onClick={() => setIsSignUp(true)}
                          >
                            Sign up
                          </Button>
                        </>
                      ) : (
                        <>
                          Already have an account?{" "}
                          <Button
                            variant="text"
                            sx={{
                              ...boldFont,
                              fontSize: getRelativeFontSize(0),
                              textTransform: "none",
                              color: BRAND,
                              "&:hover": {
                                backgroundColor: alpha(BRAND, 0.08),
                              },
                            }}
                            onClick={() => setIsSignUp(false)}
                          >
                            Sign in
                          </Button>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </motion.div>
      </Box>
    </>
  );
};

export default Login;
